import { useState, useEffect } from 'react';
import { eventsApi } from '../../lib/api';
import { supabase } from '../../lib/supabase';

const cardStyle = { background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(24px)', border: '1px solid #E2E8F0', borderRadius: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 32 };
const inputStyle = { width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #E2E8F0', fontSize: 15, fontFamily: 'Poppins, sans-serif', background: 'rgba(255,255,255,0.7)', color: '#0F172A', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#0F172A', marginBottom: 6, fontFamily: 'Poppins, sans-serif' };
const hintStyle = { fontSize: 12, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', marginTop: 4 };

export default function SubmissionForm({ eventId }) {
  const [eventData, setEventData] = useState(null);

  useEffect(() => {
    const loadEvent = async () => {
      const { data } = await supabase
        .from('event_pages')
        .select('event_name, submission_terms')
        .eq('slug', eventId)
        .single();
      if (data) setEventData(data);
    };
    loadEvent();
  }, [eventId]);

  const submissionTerms = eventData?.submission_terms;
  const [form, setForm] = useState({
    product_name: '', studio_name: '', inventor_name: '', inventor_email: '',
    already_pitched_to: '', suggested_company_1: '', suggested_company_2: '', suggested_company_3: '',
    sizzle_video_url: '', backup_description: '', event_terms_accepted: false, platform_terms_accepted: false,
  });
  const [pptFile, setPptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.inventor_name || !form.inventor_email || !form.product_name) {
      setError('Name, email, and product name are required');
      return;
    }
    if (!form.event_terms_accepted || !form.platform_terms_accepted) {
      setError('You must accept both the event terms and the platform terms to submit');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('inventor_name', form.inventor_name);
      formData.append('inventor_email', form.inventor_email);
      formData.append('product_name', form.product_name);
      formData.append('studio_name', form.studio_name);
      formData.append('already_pitched_to', JSON.stringify(form.already_pitched_to.split(',').map(s => s.trim()).filter(Boolean)));
      formData.append('suggested_companies', JSON.stringify([form.suggested_company_1, form.suggested_company_2, form.suggested_company_3].filter(Boolean)));
      formData.append('sizzle_video_url', form.sizzle_video_url);
      formData.append('backup_description', form.backup_description);
      formData.append('terms_accepted', 'true');
      if (pptFile) formData.append('ppt_file', pptFile);

      await eventsApi.submitPublic(eventId, formData);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <div style={{ ...cardStyle, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 style={{ fontFamily: '"EB Garamond", serif', fontSize: 28, color: '#0F172A', margin: '0 0 8px' }}>Submission Received!</h2>
            <p style={{ fontSize: 14, color: '#64748B', fontFamily: 'Poppins, sans-serif', lineHeight: 1.6 }}>
              Thank you for submitting your idea. The event organizer will review your submission and notify you about next steps via email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '40px 16px' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg, #c9a84c, #F59E0B)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', boxShadow: '0 4px 16px rgba(201,168,76,0.3)',
          }}>
            <span style={{ color: 'white', fontWeight: 700, fontSize: 18, fontFamily: 'Montserrat, sans-serif' }}>PV</span>
          </div>
          <h1 style={{ fontFamily: '"EB Garamond", serif', fontSize: 28, color: '#0F172A', margin: '0 0 8px' }}>{eventData?.event_name || 'Submit Your Idea'}</h1>
          <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>Submit your toy or game idea</p>
        </div>

        <div style={cardStyle}>
          {error && (
            <div style={{ marginBottom: 20, padding: 12, borderRadius: 12, background: '#FEF2F2', border: '1px solid #FECACA', color: '#F97066', fontSize: 13, fontFamily: 'Poppins, sans-serif' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Product info */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Name of Your Product and Your Company/Inventor Studio *</label>
              <input value={form.product_name} onChange={e => setForm({ ...form, product_name: e.target.value })}
                placeholder='e.g. "Monopoly by Hasbro" OR "Feed The Animals by The Toy Coach Studios"' style={inputStyle} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>Your First & Last Name *</label>
                <input value={form.inventor_name} onChange={e => setForm({ ...form, inventor_name: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Your Email *</label>
                <input type="email" value={form.inventor_email} onChange={e => setForm({ ...form, inventor_email: e.target.value })} style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Studio/Company Name</label>
              <input value={form.studio_name} onChange={e => setForm({ ...form, studio_name: e.target.value })} style={inputStyle} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Who have you ALREADY pitched this idea to?</label>
              <input value={form.already_pitched_to} onChange={e => setForm({ ...form, already_pitched_to: e.target.value })}
                placeholder="Separate with commas" style={inputStyle} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>List 3 Toy Companies/Retailers That You Think Would Love Your Product</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input value={form.suggested_company_1} onChange={e => setForm({ ...form, suggested_company_1: e.target.value })} placeholder="Company 1" style={inputStyle} />
                <input value={form.suggested_company_2} onChange={e => setForm({ ...form, suggested_company_2: e.target.value })} placeholder="Company 2" style={inputStyle} />
                <input value={form.suggested_company_3} onChange={e => setForm({ ...form, suggested_company_3: e.target.value })} placeholder="Company 3" style={inputStyle} />
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Link To Your Product Sizzle Video or Product Link</label>
              <input value={form.sizzle_video_url} onChange={e => setForm({ ...form, sizzle_video_url: e.target.value })}
                placeholder="Dropbox, YouTube, or other URL" style={inputStyle} />
              <p style={hintStyle}>Dropbox link works best</p>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Attach Your Product PPT (if you have one)</label>
              <input type="file" accept=".ppt,.pptx,.pdf,.doc,.docx" onChange={e => setPptFile(e.target.files[0])}
                style={{ ...inputStyle, padding: 10 }} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>If no video or presentation, describe your concept here</label>
              <textarea value={form.backup_description} onChange={e => setForm({ ...form, backup_description: e.target.value })}
                rows={4} placeholder="Describe what your video will cover..." style={{ ...inputStyle, resize: 'none' }} />
            </div>

            {/* Event Terms of Submission */}
            {submissionTerms && (
              <div style={{ marginBottom: 20, padding: 16, background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0' }}>
                <label style={{ ...labelStyle, marginBottom: 8 }}>Event Terms of Submission *</label>
                <p style={{ fontSize: 12, color: '#64748B', fontFamily: 'Poppins, sans-serif', lineHeight: 1.6, margin: '0 0 12px', whiteSpace: 'pre-wrap' }}>
                  {submissionTerms}
                </p>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.event_terms_accepted}
                    onChange={e => setForm({ ...form, event_terms_accepted: e.target.checked })}
                    style={{ accentColor: '#c9a84c', marginTop: 2 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>
                    I accept the Event Terms of Submission *
                  </span>
                </label>
              </div>
            )}

            {/* Platform Terms & Privacy */}
            <div style={{ marginBottom: 24, padding: 16, background: '#F8FAFC', borderRadius: 12, border: '1px solid #E2E8F0' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.platform_terms_accepted}
                  onChange={e => setForm({ ...form, platform_terms_accepted: e.target.checked })}
                  style={{ accentColor: '#c9a84c', marginTop: 2 }} />
                <span style={{ fontSize: 13, color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>
                  I accept the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#c9a84c', fontWeight: 600, textDecoration: 'underline' }}>Terms & Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#c9a84c', fontWeight: 600, textDecoration: 'underline' }}>Privacy Policy</a> of PlayVentr *
                </span>
              </label>
            </div>

            <button type="submit" disabled={submitting} style={{
              width: '100%', padding: '14px 28px', borderRadius: 14,
              background: '#c9a84c', color: 'white', fontSize: 15, fontWeight: 600,
              fontFamily: 'Poppins, sans-serif', border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.5 : 1, boxShadow: '0 2px 8px rgba(201,168,76,0.3)',
            }}>
              {submitting ? 'Submitting...' : 'Submit Your Idea'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
